import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { applyNoticeTemplate, listNoticeDefinitions } from "@/features/notices/api";
import type { NoticeDefinitionOption } from "@/features/notices/types";

interface ApplyTemplateDialogProps {
  noticeId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onApplied: () => void;
}

/**
 * Replace this notice's action steps with the firm step template for a chosen
 * notice code. Warns that the current steps are overwritten.
 */
export function ApplyTemplateDialog({
  noticeId,
  open,
  onOpenChange,
  onApplied,
}: ApplyTemplateDialogProps) {
  const [definitions, setDefinitions] = useState<NoticeDefinitionOption[]>([]);
  const [code, setCode] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setCode("");
    let active = true;
    listNoticeDefinitions()
      .then((list) => active && setDefinitions(list))
      .catch(() => active && setDefinitions([]));
    return () => {
      active = false;
    };
  }, [open]);

  async function apply() {
    if (!code) return;
    setSaving(true);
    try {
      await applyNoticeTemplate(noticeId, code);
      toast.success("Template applied");
      onApplied();
      onOpenChange(false);
    } catch {
      toast.error("Couldn't apply the template.");
    } finally {
      setSaving(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Apply step template</DialogTitle>
          <DialogDescription>
            Replaces this notice's current steps with your firm's template for the
            selected code.
          </DialogDescription>
        </DialogHeader>
        <div className="grid gap-2">
          <Label>Notice code</Label>
          <Select value={code} onValueChange={setCode}>
            <SelectTrigger>
              <SelectValue placeholder="Select a notice code…" />
            </SelectTrigger>
            <SelectContent>
              {definitions.map((d) => (
                <SelectItem key={d.id} value={d.code}>
                  {d.code} — {d.title}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={apply} disabled={!code || saving}>
            Apply
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
